'use client';

import React, { useEffect, useRef } from 'react';

const services = [
  {
    id: 'detailing-wnetrza',
    icon: '🚗',
    title: 'Detailing Wnętrza',
    desc: 'Kompleksowe czyszczenie kabiny — odkurzanie, czyszczenie plastików, szyb i tapicerek. Twoje wnętrze jak nowe.',
    tag: 'Najpopularniejszy',
    tagColor: 'bg-kd-cyan/10 text-kd-cyan border-kd-cyan/30',
    size: 'lg',
  },
  {
    id: 'pranie-tapicerki',
    icon: '🧽',
    title: 'Pranie Tapicerki',
    desc: 'Ekstrakcyjne pranie foteli i dywanów. Usuwamy plamy, zapachy i bakterie.',
    tag: null,
    size: 'sm',
  },
  {
    id: 'skory',
    icon: '🪑',
    title: 'Czyszczenie Skór',
    desc: 'Delikatne czyszczenie i impregnacja skórzanej tapicerki. Przywracamy elastyczność i blask.',
    tag: null,
    size: 'sm',
  },
  {
    id: 'korekta-lakieru',
    icon: '✨',
    title: 'Korekta Lakieru',
    desc: 'Usuwamy rysy, wiry i hologramy. Wielostopniowa polerka maszynowa przywraca głębię koloru.',
    tag: 'Premium',
    tagColor: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    size: 'md',
  },
  {
    id: 'powloki',
    icon: '🛡️',
    title: 'Powłoki Ochronne',
    desc: 'Ceramiczne i kwarcowe powłoki ochronne. Ochrona lakieru na lata.',
    tag: null,
    size: 'md',
  },
  {
    id: 'meble',
    icon: '🛋️',
    title: 'Tapicerka Meblowa',
    desc: 'Pranie i czyszczenie mebli tapicerowanych — kanapy, fotele, krzesła.',
    tag: null,
    size: 'sm',
  },
  {
    id: 'sprzedaz',
    icon: '🏷️',
    title: 'Przygotowanie do Sprzedaży',
    desc: 'Kompleksowe przygotowanie pojazdu — zwiększamy wartość rynkową Twojego auta.',
    tag: 'Nowe',
    tagColor: 'bg-green-500/10 text-green-400 border-green-500/30',
    size: 'sm',
  },
];

const pricing = [
  { icon: '🚿', label: 'Mycie / Sprzątanie', price: 'od 100 PLN' },
  { icon: '🧽', label: 'Pranie', price: 'od 300 PLN' },
  { icon: '🛡️', label: 'Powłoka', price: 'od 600 PLN' },
  { icon: '✨', label: 'Polerowanie', price: 'od 600 PLN' },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    const els = sectionRef?.current?.querySelectorAll('.reveal-hidden, .reveal-scale');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="uslugi" className="py-24 md:py-32 bg-kd-dark">
      <div className="max-w-container mx-auto px-6">
        {/* Header */}
        <div className="mb-16 reveal-hidden">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-kd-cyan" />
            <span className="text-kd-cyan text-xs font-body font-semibold uppercase tracking-widest">
              Nasze Usługi
            </span>
          </div>
          <h2 className="font-heading font-black uppercase text-white mb-4" style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)', lineHeight: '0.95' }}>
            CO MOŻEMY<br />
            <span className="gradient-text">DLA CIEBIE</span>
          </h2>
          <p className="text-kd-muted font-body text-lg max-w-xl">
            Oferujemy pełen zakres usług detailingowych — od mycia po zaawansowaną ochronę ceramiczną.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Large card — detailing wnętrza */}
          <div className="md:col-span-2 service-card rounded-2xl p-7 reveal-hidden relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-kd-cyan/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <span className="text-4xl mb-4 block">{services?.[0]?.icon}</span>
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-heading font-bold text-white text-3xl uppercase tracking-wide">
                  {services?.[0]?.title}
                </h3>
                {services?.[0]?.tag && (
                  <span className={`text-xs font-body font-semibold px-3 py-1 rounded-full border ${services?.[0]?.tagColor}`}>
                    {services?.[0]?.tag}
                  </span>
                )}
              </div>
              <p className="text-kd-muted font-body leading-relaxed text-base">
                {services?.[0]?.desc}
              </p>
              <div className="mt-6 flex items-center gap-2 text-kd-cyan text-sm font-body font-medium group-hover:gap-4 transition-all">
                <span>Zapytaj o cenę</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Small card */}
          <div className="service-card rounded-2xl p-6 reveal-hidden relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-kd-cyan/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <span className="text-3xl mb-3 block">{services?.[1]?.icon}</span>
              <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-wide mb-2">
                {services?.[1]?.title}
              </h3>
              <p className="text-kd-muted font-body text-sm leading-relaxed">
                {services?.[1]?.desc}
              </p>
            </div>
          </div>

          {/* Small card */}
          <div className="service-card rounded-2xl p-6 reveal-hidden relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-kd-cyan/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <span className="text-3xl mb-3 block">{services?.[2]?.icon}</span>
              <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-wide mb-2">
                {services?.[2]?.title}
              </h3>
              <p className="text-kd-muted font-body text-sm leading-relaxed">
                {services?.[2]?.desc}
              </p>
            </div>
          </div>

          {/* Medium card — korekta */}
          <div className="service-card rounded-2xl p-6 reveal-hidden relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <span className="text-3xl mb-3 block">{services?.[3]?.icon}</span>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-wide">
                  {services?.[3]?.title}
                </h3>
                <span className={`text-xs font-body font-semibold px-2 py-1 rounded-full border ${services?.[3]?.tagColor}`}>
                  {services?.[3]?.tag}
                </span>
              </div>
              <p className="text-kd-muted font-body text-sm leading-relaxed">
                {services?.[3]?.desc}
              </p>
            </div>
          </div>

          {/* Medium card — powłoki */}
          <div className="service-card rounded-2xl p-6 reveal-hidden relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-kd-cyan/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <span className="text-3xl mb-3 block">{services?.[4]?.icon}</span>
              <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-wide mb-2">
                {services?.[4]?.title}
              </h3>
              <p className="text-kd-muted font-body text-sm leading-relaxed">
                {services?.[4]?.desc}
              </p>
            </div>
          </div>

          {/* Small card — meble */}
          <div className="service-card rounded-2xl p-6 reveal-hidden relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-kd-cyan/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              <span className="text-3xl mb-3 block">{services?.[5]?.icon}</span>
              <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-wide mb-2">
                {services?.[5]?.title}
              </h3>
              <p className="text-kd-muted font-body text-sm leading-relaxed">
                {services?.[5]?.desc}
              </p>
            </div>
          </div>

          {/* Sprzedaz + CTA combined */}
          <div className="md:col-span-2 service-card rounded-2xl p-6 reveal-hidden relative overflow-hidden group flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1">
              <span className="text-3xl mb-3 block">{services?.[6]?.icon}</span>
              <div className="flex items-start gap-3 mb-2">
                <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-wide">
                  {services?.[6]?.title}
                </h3>
                <span className={`text-xs font-body font-semibold px-2 py-1 rounded-full border whitespace-nowrap ${services?.[6]?.tagColor}`}>
                  {services?.[6]?.tag}
                </span>
              </div>
              <p className="text-kd-muted font-body text-sm leading-relaxed">
                {services?.[6]?.desc}
              </p>
            </div>
            <div className="shrink-0">
              <a
                href="tel:+48731655388"
                className="btn-primary relative inline-flex items-center gap-2 px-6 py-3 rounded-xl font-heading font-bold text-white uppercase tracking-wider text-lg z-10"
              >
                Zadzwoń
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="mt-16 reveal-hidden">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-kd-cyan" />
            <span className="text-kd-cyan text-xs font-body font-semibold uppercase tracking-widest">
              Cennik
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pricing?.map((item) => (
              <div
                key={item?.label}
                className="service-card rounded-2xl p-6 flex flex-col items-center text-center group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-kd-cyan/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="text-3xl mb-3">{item?.icon}</span>
                <p className="text-kd-muted font-body text-sm mb-3 leading-snug">{item?.label}</p>
                <p className="font-heading font-black text-kd-cyan text-2xl uppercase tracking-wide">{item?.price}</p>
              </div>
            ))}
          </div>
          <p className="text-kd-muted font-body text-xs mt-4 text-center">
            * Ceny orientacyjne. Ostateczna wycena zależy od stanu i rozmiaru pojazdu.
          </p>
        </div>
      </div>
    </section>
  );
}